import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { load_script } from "../utils";
import PropTypes from 'prop-types';
// Callback for when reCAPTCHA script is loaded
const loadCallback = "recaptcha_load_callback";

// Callback for when reCAPTCHA is clicked
const clickCallback = "recaptcha_click_callback";

// Recaptcha component to render Google reCAPTCHA
export const Recaptcha = forwardRef(
  ({ onChange, className, elementId = "google-recaptcha", errors }, ref) => {
    useImperativeHandle(ref, () => ({
      resetRecaptcha() {
        if (
          globalThis.grecaptcha &&
          typeof globalThis.grecaptcha.reset === "function"
        ) {
          globalThis.grecaptcha.reset();
        }
      }
    }));

    useEffect(() => {
      // Function to render reCAPTCHA, ensuring grecaptcha is available
      const recaptchaRender = () => {
        if (
          globalThis.grecaptcha &&
          typeof globalThis.grecaptcha.render === "function"
        ) {
          try {
            globalThis.grecaptcha.render(elementId, {
              sitekey: process.env.REACT_APP_RECAPTCHA_KEY,
              callback: globalThis[clickCallback]
            });
          } catch (error) {
            if (
              error.message !==
              "reCAPTCHA has already been rendered in this element"
            ) {
              throw error;
            }
          }
        } else {
          setTimeout(recaptchaRender, 500); // Retry after 500ms
        }
      };

      const onScriptLoad = () => {
        if (
          globalThis.grecaptcha &&
          typeof globalThis.grecaptcha.render === "function"
        ) {
          // If grecaptcha is already available, render immediately
          recaptchaRender();
        } else {
          // Set the callback and load the script
          globalThis[loadCallback] = recaptchaRender;
          load_script(
            `https://www.google.com/recaptcha/api.js?onload=${loadCallback}&render=explicit`
          );
        }
      };

      // Sets click callback to handle reCAPTCHA response
      globalThis[clickCallback] = response => {
        if (onChange) {
          onChange(response);
        }
      };

      // Load reCAPTCHA script
      onScriptLoad();

      // Cleanup function to remove callbacks on unmount
      return () => {
        delete globalThis[clickCallback];
        delete globalThis[loadCallback];
      };
    }, [elementId, onChange]);

    return (
      <>
        <div className={className} id={elementId} data-testid="presentation"/>
        <div>
          {errors && (
            <span className="recaptcha-error ageo-medium">{errors["recaptcha_token"]}</span>
          )}
        </div>
      </>
    );
  }
);

Recaptcha.propTypes = {
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  elementId: PropTypes.string,
  errors: PropTypes.object,
};

